import React, { useMemo } from 'react';
import { Avatar, Box, Button, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { stringAvatar } from '../../../utils/AvatarUtils';
import { FormSurface, useFormPalette, StatusPill, formatProgressTone } from './DynamicFormPrimitives';

const AVATAR_SIZE = 28;
const AVATAR_RADIUS = '6px';
const TEXT_INDENT = '38px';

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

function resolveTitle(parsedArgs, parsedResult) {
  return (
    parsedResult?.form?.title ||
    parsedArgs?.title ||
    'Review Task'
  );
}

function resolveObjective(parsedArgs, parsedResult) {
  return (
    parsedResult?.form?.objective ||
    parsedArgs?.objective ||
    parsedArgs?.description ||
    null
  );
}

export default function ReviewTaskCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  onOpenForm = null,
  onOpenAgentSettings = null,
  lifecycleState,
}) {
  const parsedArgs = useMemo(() => parseMaybeJson(args), [args]);
  const parsedResult = useMemo(() => parseMaybeJson(result), [result]);
  const inferredFormId =
    parsedResult?.formInstanceId ||
    parsedResult?.form?.formInstanceId ||
    parsedArgs?.formInstanceId ||
    null;
  const toolError = parsedResult?.error || null;
  const hasToolError = Boolean(toolError);
  const title = resolveTitle(parsedArgs, parsedResult);
  const objective = resolveObjective(parsedArgs, parsedResult);
  const progressStatus =
    parsedResult?.form?.progressStatus ||
    null;
  const isRunning = lifecycleState === 'running' || lifecycleState === 'partial';
  const canOpen = Boolean(inferredFormId && typeof onOpenForm === 'function');

  const avatarClick = agentId && typeof onOpenAgentSettings === 'function'
    ? (e) => { e.stopPropagation(); onOpenAgentSettings(agentId); }
    : undefined;

  const palette = useFormPalette();

  const handleOpen = () => {
    if (!canOpen) return;
    onOpenForm({
      formId: inferredFormId,
      args,
      result,
      title,
      progressStatus,
    });
  };

  return (
    <Box sx={{ px: 2.5, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box sx={{ pl: grouped ? TEXT_INDENT : 0, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        {!grouped && (
          <Avatar
            variant="rounded"
            {...stringAvatar(agentName || 'Agent')}
            onClick={avatarClick}
            sx={{
              ...stringAvatar(agentName || 'Agent').sx,
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              borderRadius: AVATAR_RADIUS,
              fontSize: '12px',
              fontWeight: 600,
              flexShrink: 0,
              ...(avatarClick ? {
                cursor: 'pointer',
                transition: 'transform 140ms ease, box-shadow 140ms ease',
                '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(24,24,27,0.2)' },
                '&:active': { transform: 'translateY(0)' },
              } : {}),
            }}
          />
        )}
        <Box sx={{ width: '75%', minWidth: '280px' }}>
          <FormSurface
            title={title}
            subtitle={objective || (isRunning ? 'Creating review task…' : 'Human review task')}
            rightSlot={progressStatus ? (
              <StatusPill label={progressStatus} tone={formatProgressTone(progressStatus)} />
            ) : null}
          >
            {hasToolError && (
              <Typography sx={{ fontSize: '12px', color: palette.errorText, fontWeight: 600, mb: 1 }}>
                {toolError}
              </Typography>
            )}

            {!isRunning && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canOpen}
                  onClick={handleOpen}
                  startIcon={<RateReviewOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                >
                  Open review
                </Button>
              </Box>
            )}
          </FormSurface>
        </Box>
      </Box>
    </Box>
  );
}
